
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ColumnInfo } from "@/types/dbSchema";
import { TableStructureViewer } from "./TableStructureViewer";
import { TroubleshootingTips } from "./TroubleshootingTips";
import { callUntypedRpcFunction } from "@/utils/rpcUtils";

interface StructureCheckerProps {
  expectedColumns: {
    column_name: string;
    data_type: string;
    is_nullable: string;
  }[];
}

export function StructureChecker({ expectedColumns }: StructureCheckerProps) {
  const [showTableStructure, setShowTableStructure] = useState<boolean>(false);
  const [tableColumns, setTableColumns] = useState<ColumnInfo[]>([]);
  const [isLoadingStructure, setIsLoadingStructure] = useState<boolean>(false);
  
  // Updated expected columns to match actual Supabase schema
  const actualExpectedColumns = [
    { column_name: 'id', data_type: 'uuid', is_nullable: 'NO' },
    { column_name: 'customer_name', data_type: 'text', is_nullable: 'NO' },
    { column_name: 'email', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'address', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'postcode', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'bin_day', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'start_date', data_type: 'timestamp with time zone', is_nullable: 'YES' },
    { column_name: 'weekly_rate', data_type: 'numeric', is_nullable: 'YES' },
    { column_name: 'total_paid', data_type: 'numeric', is_nullable: 'YES' },
    { column_name: 'created_at', data_type: 'timestamp with time zone', is_nullable: 'YES' },
    { column_name: 'updated_at', data_type: 'timestamp with time zone', is_nullable: 'YES' },
    { column_name: 'active', data_type: 'boolean', is_nullable: 'YES' },
    { column_name: 'status', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'end_date', data_type: 'timestamp with time zone', is_nullable: 'YES' },
    { column_name: 'is_business', data_type: 'boolean', is_nullable: 'YES' },
    { column_name: 'business_name', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'multiple_days', data_type: 'boolean', is_nullable: 'YES' },
    { column_name: 'is_annual', data_type: 'boolean', is_nullable: 'YES' },
    // NEW COLUMNS
    { column_name: 'house_number', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'street', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'town_city', data_type: 'text', is_nullable: 'YES' },
    { column_name: 'subscription_type', data_type: 'text', is_nullable: 'YES' }
  ];
  
  const fetchTableStructure = async () => {
    setIsLoadingStructure(true);
    try {
      // Try direct query first and log the results
      const { data: directData, error: directError } = await supabase
        .from('subscriptions')
        .select('*')
        .limit(1);
        
      if (directError) {
        console.error("Direct table query error:", directError);
        toast({
          title: "Table access error",
          description: "Could not access the subscriptions table: " + directError.message,
          variant: "destructive"
        });
      } else {
        console.log("Direct table query succeeded:", directData);
        
        // If we can query the table, try to get its structure directly using untyped RPC function
        try {
          // Use callUntypedRpcFunction instead of supabase.rpc directly
          const rpcResult = await callUntypedRpcFunction<ColumnInfo[]>('get_table_structure', {
            table_name: 'subscriptions'
          });
          
          if (rpcResult.error) {
            throw new Error("Failed to get table structure: " + rpcResult.error.message);
          } else {
            setTableColumns(rpcResult.data || []);
          }
        } catch (rpcError) {
          console.error("Error fetching table structure:", rpcError);
          
          // As a final fallback, try to infer structure from the sample data
          if (directData && directData.length > 0) {
            console.log("Inferring structure from sample data");
            const sampleRecord = directData[0];
            const inferredColumns: ColumnInfo[] = Object.keys(sampleRecord).map(key => {
              const value = sampleRecord[key];
              let dataType = typeof value;
              
              // Fix type casting for special cases
              if (dataType === 'object') {
                if (value instanceof Date) {
                  // Use type assertion to specify PostgreSQL type in the column_info
                  return {
                    column_name: key,
                    data_type: "timestamp with time zone",
                    is_nullable: 'YES'
                  };
                } else if (value === null) {
                  return {
                    column_name: key,
                    data_type: "null",
                    is_nullable: 'YES'
                  };
                }
              }
              
              return {
                column_name: key,
                data_type: dataType,
                is_nullable: 'YES'
              };
            });
            
            setTableColumns(inferredColumns);
          } else {
            setTableColumns([]);
          }
        }
      }
    } catch (err) {
      console.error("Error checking table structure:", err);
      toast({
        title: "Error",
        description: "Failed to check table structure: " + (err instanceof Error ? err.message : String(err)),
        variant: "destructive"
      });
      setTableColumns([]);
    } finally {
      setIsLoadingStructure(false);
    }
  };

  const toggleTableStructure = () => {
    const newState = !showTableStructure;
    setShowTableStructure(newState);
    if (newState && tableColumns.length === 0) {
      fetchTableStructure();
    }
  };
  
  return (
    <div className="w-full mt-4">
      <Button
        onClick={toggleTableStructure}
        variant="outline"
        className="w-full justify-between"
      >
        <span>
          <Info className="h-4 w-4 inline mr-2" />
          {showTableStructure ? "Hide" : "Check"} Supabase Table Structure
        </span>
        {isLoadingStructure && <span className="animate-spin">⟳</span>}
      </Button>
      
      {showTableStructure && (
        <div className="mt-2 border rounded p-2 bg-gray-50 text-sm">
          <h3 className="font-semibold mb-2">Subscriptions Table Structure</h3>
          
          <TableStructureViewer
            tableColumns={tableColumns}
            isLoadingStructure={isLoadingStructure}
            expectedColumns={actualExpectedColumns}
          />
          
          <TroubleshootingTips />
        </div>
      )}
    </div>
  );
}
